import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Filter, Grid, List, Play, Edit2, Trash2, X } from 'lucide-react';
import { videoApi } from '../services/api';
import type { Video } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';

interface VideoLibraryProps {
  onVideoSelect?: (video: Video) => void;
  selectionMode?: boolean;
}

interface VideoCardProps {
  video: Video;
  onEdit: (video: Video) => void;
  onDelete: (videoId: string) => void;
  onPreview: (video: Video) => void;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (video: Video) => void;
}

function VideoCard({ video, onEdit, onDelete, onPreview, selectable, onSelect }: VideoCardProps) {
  const [isHovering, setIsHovering] = useState(false);

  const categoryColors = {
    strength: 'bg-red-100 text-red-800',
    cardio: 'bg-blue-100 text-blue-800',
    mobility: 'bg-green-100 text-green-800',
    flexibility: 'bg-purple-100 text-purple-800',
    other: 'bg-gray-100 text-gray-800',
  };

  const difficultyColors = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800',
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Use signed thumbnail URLs from API (required for signed Mux playback IDs)
  const thumbnailUrl = video.thumbnail_url ?? null;
  const animatedThumbnailUrl = video.animated_thumbnail_url ?? null;

  return (
    <div
      className={`bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden ${
        selectable ? 'cursor-pointer' : ''
      }`}
      onClick={() => selectable && onSelect && onSelect(video)}
    >
      {/* Thumbnail */}
      <div 
        className="relative aspect-video bg-gray-200 flex items-center justify-center overflow-hidden"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {thumbnailUrl ? (
          <>
            {/* Static thumbnail - always visible */}
            <img
              src={thumbnailUrl}
              alt={video.title}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                isHovering ? 'opacity-0' : 'opacity-100'
              }`}
            />
            {/* Animated preview on hover */}
            {animatedThumbnailUrl && (
              <img
                src={animatedThumbnailUrl}
                alt={`${video.title} preview`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                  isHovering ? 'opacity-100' : 'opacity-0'
                }`}
              />
            )}
          </>
        ) : (
          <Play className="w-12 h-12 text-gray-400" />
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPreview(video);
          }}
          className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center group"
        >
          <Play className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
        {video.duration_seconds > 0 && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
            {formatDuration(video.duration_seconds)}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 truncate mb-2">{video.title}</h3>
        {video.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{video.description}</p>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className={`text-xs px-2 py-1 rounded-full ${categoryColors[video.category]}`}>
            {video.category}
          </span>
          <span className={`text-xs px-2 py-1 rounded-full ${difficultyColors[video.difficulty]}`}>
            {video.difficulty}
          </span>
        </div>

        {/* Actions */}
        {!selectable && (
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(video);
              }}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(video.id);
              }}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface VideoPreviewModalProps {
  video: Video;
  onClose: () => void;
}

function VideoPreviewModal({ video, onClose }: VideoPreviewModalProps) {
  const { data: playbackData, isError, error } = useQuery({
    queryKey: ['videoPlayback', video.id],
    queryFn: async () => {
      const response = await videoApi.getPlaybackUrl(video.id);
      return response.data;
    },
  });

  const playbackUrl = (playbackData as { playback_url?: string })?.playback_url;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">{video.title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4">
          {playbackUrl ? (
            <video
              src={playbackUrl}
              controls
              className="w-full aspect-video bg-black rounded"
            />
          ) : isError ? (
            <div className="w-full aspect-video bg-gray-200 flex items-center justify-center rounded">
              <p className="text-red-600">Failed to load video. {(error as { message?: string })?.message}</p>
            </div>
          ) : (
            <div className="w-full aspect-video bg-gray-200 flex items-center justify-center rounded">
              <p className="text-gray-500">Loading video...</p>
            </div>
          )}

          {video.description && (
            <div className="mt-4">
              <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-gray-600">{video.description}</p>
            </div>
          )}

          <div className="mt-4 flex gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">Category:</span> {video.category}
            </div>
            <div>
              <span className="font-medium">Difficulty:</span> {video.difficulty}
            </div>
            {video.duration_seconds > 0 && (
              <div>
                <span className="font-medium">Duration:</span>{' '}
                {Math.floor(video.duration_seconds / 60)}:{(video.duration_seconds % 60).toString().padStart(2, '0')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface EditVideoModalProps {
  video: Video;
  onClose: () => void;
  onSave: (videoId: string, data: Partial<Video>) => void;
}

function EditVideoModal({ video, onClose, onSave }: EditVideoModalProps) {
  const [formData, setFormData] = useState({
    title: video.title,
    description: video.description || '',
    category: video.category,
    difficulty: video.difficulty,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(video.id, formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Edit Video</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700">
              Title
            </label>
            <input
              id="edit-title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>

          <div>
            <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="edit-description"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="edit-category" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                id="edit-category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as Video['category'] })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="strength">Strength</option>
                <option value="cardio">Cardio</option>
                <option value="mobility">Mobility</option>
                <option value="flexibility">Flexibility</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="edit-difficulty" className="block text-sm font-medium text-gray-700">
                Difficulty
              </label>
              <select
                id="edit-difficulty"
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as Video['difficulty'] })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function VideoLibrary({ onVideoSelect, selectionMode = false }: VideoLibraryProps) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);
  const [editVideo, setEditVideo] = useState<Video | null>(null);

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Fetch videos with filters
  const { data: videos, isLoading } = useQuery({
    queryKey: ['videos', categoryFilter, difficultyFilter, debouncedSearchQuery],
    queryFn: async () => {
      const params: any = {};
      if (categoryFilter) params.category = categoryFilter;
      if (difficultyFilter) params.difficulty = difficultyFilter;
      if (debouncedSearchQuery) params.search = debouncedSearchQuery;
      
      const response = await videoApi.list(params);
      const data = response.data;
      // Backend returns paginated { data: [...], ... } by default; extract the array
      return Array.isArray(data) ? data : (data?.data ?? []);
    },
  });

  // Update video mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Video> }) =>
      videoApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      setEditVideo(null);
    },
  });

  // Delete video mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => videoApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
    },
  });

  const handleDelete = (videoId: string) => {
    if (confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      deleteMutation.mutate(videoId);
    }
  };

  const handleVideoSelect = (video: Video) => {
    if (selectionMode && onVideoSelect) {
      onVideoSelect(video);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
    setDifficultyFilter('');
  };

  const hasActiveFilters = searchQuery || categoryFilter || difficultyFilter;

  return (
    <div className="space-y-4">
      {/* Search and Filters Bar */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-md transition-colors ${
              showFilters || hasActiveFilters
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-5 h-5" />
            Filters
            {hasActiveFilters && (
              <span className="bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {[categoryFilter, difficultyFilter, searchQuery].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* View Mode Toggle */}
          <div className="flex border border-gray-300 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 ${
                viewMode === 'grid'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 ${
                viewMode === 'list'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  id="category-filter"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="">All Categories</option>
                  <option value="strength">Strength</option>
                  <option value="cardio">Cardio</option>
                  <option value="mobility">Mobility</option>
                  <option value="flexibility">Flexibility</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="difficulty-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty
                </label>
                <select
                  id="difficulty-filter"
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="">All Difficulties</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 text-sm text-indigo-600 hover:text-indigo-800"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Video Grid/List */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading videos...</p>
        </div>
      ) : !videos || videos.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Play className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            {hasActiveFilters ? 'No videos found matching your filters' : 'No videos yet'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-2 text-indigo-600 hover:text-indigo-800"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-4'
          }
        >
          {videos.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              onEdit={setEditVideo}
              onDelete={handleDelete}
              onPreview={setPreviewVideo}
              selectable={selectionMode}
              onSelect={handleVideoSelect}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {previewVideo && (
        <VideoPreviewModal
          video={previewVideo}
          onClose={() => setPreviewVideo(null)}
        />
      )}

      {editVideo && (
        <EditVideoModal
          video={editVideo}
          onClose={() => setEditVideo(null)}
          onSave={(id, data) => updateMutation.mutate({ id, data })}
        />
      )}
    </div>
  );
}
